import type { DemandeDeVerification } from '@/lib/api/auth'

/**
 * La vérification d'adresse en cours, retenue le temps de l'onglet — §324.
 *
 * **Ce qui arrivait aux candidats.** On leur envoie un code par courriel ; ils
 * quittent l'écran pour aller le lire dans leur boîte ; le navigateur du
 * téléphone, à court de mémoire, DÉCHARGE la page ; au retour elle se recharge,
 * l'état du composant est perdu, et l'application les ramène à l'onglet
 * « Se connecter » — sans compte, et sans plus aucun moyen de saisir le code
 * qu'ils tiennent sous les yeux.
 *
 * Deux défauts se cumulaient. L'étape n'était nulle part conservée ; et
 * l'adresse portait encore `?se-connecter` quand le candidat était arrivé par
 * ce bouton avant de basculer sur l'onglet d'inscription — au rechargement,
 * c'est donc la connexion qui s'ouvrait.
 *
 * **`sessionStorage`, et pas autre chose.** `localStorage` survivrait des
 * semaines et proposerait un code expiré à quelqu'un qui revient le mois
 * suivant. Un paramètre d'adresse se partagerait par copier-coller. Un état de
 * composant ne survit pas au rechargement, ce qui est précisément le problème.
 * `sessionStorage` vit le temps de l'onglet — exactement la durée d'une
 * inscription.
 *
 * **Le code n'est JAMAIS retenu ici**, seulement le jeton public de la demande
 * et l'adresse. Le code est la preuve ; l'écrire sur le disque du navigateur
 * reviendrait à laisser la clé sur la porte.
 */

const CLE = 'haicademy:verification-en-cours'

export interface VerificationEnCours {
  adresse: string
  demande: DemandeDeVerification
}

export function retenirLaVerification(adresse: string, demande: DemandeDeVerification): void {
  try {
    sessionStorage.setItem(CLE, JSON.stringify({ adresse, demande }))
  } catch {
    // Navigation privée, quota plein, stockage refusé : on continue sans
    // reprise plutôt que de faire échouer une inscription qui, elle, marche.
  }
}

export function verificationEnCours(): VerificationEnCours | null {
  try {
    const brut = sessionStorage.getItem(CLE)

    if (brut === null) return null

    const lu = JSON.parse(brut) as Partial<VerificationEnCours>

    // **On se méfie de ce qu'on relit.** Le contenu vient du disque : une
    // version antérieure de l'application a pu y écrire une autre forme, et
    // un objet incomplet ferait planter l'écran au montage — c'est-à-dire au
    // pire moment, celui du retour.
    if (typeof lu.adresse !== 'string' || lu.adresse === '') return null
    if (typeof lu.demande !== 'object' || lu.demande === null) return null
    if (typeof lu.demande.verification !== 'string') return null

    return { adresse: lu.adresse, demande: lu.demande }
  } catch {
    return null
  }
}

/** Le jeton a servi, ou le candidat a changé d'adresse : on n'y revient pas. */
export function oublierLaVerification(): void {
  try {
    sessionStorage.removeItem(CLE)
  } catch {
    // Voir ci-dessus : l'absence de stockage ne doit rien casser.
  }
}
